import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import TurnstileField, { TS_KEY } from "../components/TurnstileField";
import { useAuth } from "../context/AuthContext";
import { isNative } from "../native";

// Leaving the wizard mid-way keeps a local draft so families can resume later
// (nothing is submitted to the backend until the final step, same as before).
const DRAFT_KEY = "p1_onboarding_draft";
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } };

const NEEDS = [
  ["companionship", "Companionship"], ["regular_checkins", "Regular check-ins"],
  ["doctor_coordination", "Doctor coordination"], ["hospital_support", "Hospital support"],
  ["caregiver_attendant", "Caregiver / attendant"], ["physiotherapy", "Physiotherapy"],
  ["nursing", "Nursing"], ["chronic_care", "Chronic-care coordination"],
  ["emergency_preparedness", "Emergency preparedness"], ["errands", "Daily errands"], ["other", "Other"],
];

const STEPS = ["Who needs care", "Parent details", "Family details", "Care needs", "Important info", "Book assessment"];

const blankParent = (label, gender = "", living = "Lives alone") => ({
  label, name: "", age: "", gender, address: "", zone: "", city: "Jaipur", language: "Hindi", living_arrangement: living,
});

const whoToParents = (who) => {
  if (who === "both") return [blankParent("Mother", "Female", "Lives with spouse"), blankParent("Father", "Male", "Lives with spouse")];
  if (who === "mother") return [blankParent("Mother", "Female")];
  if (who === "father") return [blankParent("Father", "Male")];
  return [blankParent("Family member")];
};

export default function Onboarding() {
  const [step, setStep] = useState(() => loadDraft()?.step || 0);
  const [done, setDone] = useState(null);
  const [wa, setWa] = useState(null);
  const [waConnected, setWaConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sameAddress, setSameAddress] = useState(false);
  const [tsToken, setTsToken] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(() => {
    const base = {
      who: "both",
      parents: whoToParents("both"),
      medical_for: "0",
      family: { name: "", relation: "Son", location: "", phone: "", whatsapp: "", email: "", secondary_members: "" },
      care_needs: [],
      info: { preferred_hospital: "", doctors: "", conditions: "", allergies: "", medications: "", insurance_provider: "", insurance_policy: "", ec1_name: "", ec1_phone: "", ec1_relation: "" },
      consent_medical: false,
      whatsapp_opt_in: true,
      assessment_slot: "Weekday morning",
    };
    return loadDraft()?.data || base;
  });

  // autosave draft (resume later) — cleared on successful submit
  React.useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, data })); } catch { /* storage full/blocked */ }
  }, [step, data]);

  // logged-in app users get their account details prefilled (draft values win)
  React.useEffect(() => {
    if (!user) return;
    setData((d) => (d.family.name || d.family.email) ? d : {
      ...d, family: { ...d.family, name: user.name || "", email: user.email || "", phone: user.phone || "" },
    });
  }, [user]);

  const set = (section, key, val) => setData((d) => ({ ...d, [section]: { ...d[section], [key]: val } }));
  const setParent = (i, key, val) => setData((d) => {
    const parents = d.parents.map((p, j) => (j === i ? { ...p, [key]: val } : p));
    if (sameAddress && i === 0 && (key === "address" || key === "zone") && parents.length > 1) {
      parents[1] = { ...parents[1], [key]: val };
    }
    return { ...d, parents };
  });
  const setWho = (who) => { setSameAddress(false); setData((d) => ({ ...d, who, parents: whoToParents(who), medical_for: "0" })); };
  const toggleSameAddress = (checked) => {
    setSameAddress(checked);
    if (checked) setData((d) => (d.parents.length > 1 ? { ...d, parents: [d.parents[0], { ...d.parents[1], address: d.parents[0].address, zone: d.parents[0].zone }] } : d));
  };
  const toggleNeed = (k) => setData((d) => ({ ...d, care_needs: d.care_needs.includes(k) ? d.care_needs.filter((x) => x !== k) : [...d.care_needs, k] }));

  const valid = () => {
    if (step === 1) return data.parents.every((p) => p.name.trim() && p.address.trim());
    if (step === 2) return data.family.name && data.family.phone;
    if (step === 4) return !hasMedical() || data.consent_medical;
    return true;
  };
  const hasMedical = () => data.info.conditions || data.info.allergies || data.info.medications;

  const submit = async () => {
    if (TS_KEY && !tsToken) { toast.error("Please complete the security check."); return; }
    setBusy(true);
    try {
      const parentsPayload = data.parents.map((p) => ({ ...p, age: p.age ? Number(p.age) : null }));
      const payload = {
        who: data.who,
        parents: parentsPayload,
        parent: parentsPayload[0],
        medical_for: data.medical_for,
        family: { ...data.family, secondary_members: data.family.secondary_members ? [{ note: data.family.secondary_members }] : [] },
        care_needs: data.care_needs,
        info: {
          preferred_hospital: data.info.preferred_hospital,
          doctors: data.info.doctors ? [{ name: data.info.doctors }] : [],
          conditions: data.info.conditions ? data.info.conditions.split(",").map((s) => s.trim()).filter(Boolean) : [],
          allergies: data.info.allergies ? data.info.allergies.split(",").map((s) => s.trim()).filter(Boolean) : [],
          medications: data.info.medications ? [{ name: data.info.medications, schedule: "" }] : [],
          insurance: { provider: data.info.insurance_provider, policy_number: data.info.insurance_policy },
          emergency_contacts: data.info.ec1_name ? [{ name: data.info.ec1_name, phone: data.info.ec1_phone, relation: data.info.ec1_relation }] : [],
        },
        consent_medical: data.consent_medical,
        whatsapp_opt_in: !!data.whatsapp_opt_in,
        assessment_slot: data.assessment_slot,
        turnstile_token: tsToken,
      };
      const res = await api.post("/onboarding", payload);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* draft already gone */ }
      setDone(res.data.message);
      setWa(res.data.wa_activation || null);
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  React.useEffect(() => {
    if (!wa?.code || waConnected) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/public/wa-status/${wa.code}`);
        if (r.data.connected) setWaConnected(true);
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [wa, waConnected]);

  if (done) {
    return (
      <div className="p1-container flex justify-center py-20" data-testid="onboarding-success">
        <div className="p1-card max-w-lg p-10 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-700" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-stone-900">You're in good hands.</h1>
          <p className="mt-3 text-stone-600">{done}</p>
          {wa?.code && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-left" data-testid="wa-connect-card">
              <p className="font-heading text-lg font-bold text-stone-900">Connect ParentOne on WhatsApp</p>
              {waConnected ? (
                <p className="mt-3 flex items-center justify-center gap-2 text-sm font-bold text-emerald-800" data-testid="wa-connected">
                  <CheckCircle2 className="h-5 w-5" /> WhatsApp Connected ✓</p>
              ) : (
                <>
                  <p className="mt-2 text-sm text-stone-600">Get assessment updates, Care Manager assignment, visit reminders &amp; reports, service updates, payment confirmations and important care alerts on WhatsApp. Recommended — SMS and email remain as fallback.</p>
                  {wa.link ? (
                    <a href={wa.link} target="_blank" rel="noreferrer" className="p1-btn-primary mt-4 flex w-full justify-center" data-testid="wa-activate-btn">Activate WhatsApp Updates</a>
                  ) : (
                    <p className="mt-3 rounded-xl bg-white p-3 text-center text-sm text-stone-700" data-testid="wa-code-display">
                      Send <strong>P1-ACTIVATE {wa.code}</strong> to ParentOne&apos;s official WhatsApp Business number.</p>
                  )}
                  <p className="mt-2 text-center text-xs text-stone-400">Your activation code: <strong>{wa.code}</strong> — the message is pre-filled, just press send.</p>
                </>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-center gap-3">
            <button onClick={() => navigate(isNative() ? "/app" : "/login")} className="p1-btn-primary" data-testid="onboarding-login-cta">{isNative() ? "Open my dashboard" : "Go to Login"}</button>
            {!isNative() && <button onClick={() => navigate("/")} className="p1-btn-outline" data-testid="onboarding-home-cta">Back to Home</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p1-container max-w-2xl py-12 lg:py-16" data-testid="onboarding-page">
      <p className="p1-eyebrow">Getting started</p>
      <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-stone-900">Set up care for your parents</h1>
      <div className="mt-6 flex items-center gap-1.5" data-testid="onboarding-progress">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-emerald-800" : "bg-stone-200"}`} />
        ))}
      </div>
      <p className="mt-2 text-sm font-semibold text-stone-500">Step {step + 1} of 6 — {STEPS[step]}</p>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}
          className="p1-card mt-6 p-7">
          {step === 0 && (
            <div data-testid="step-who">
              <h2 className="font-heading text-xl font-bold text-stone-900">Who needs care?</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {[["both", "Both parents"], ["mother", "My mother"], ["father", "My father"], ["other", "Someone else"]].map(([v, l]) => (
                  <button key={v} onClick={() => setWho(v)}
                    className={`rounded-2xl border-2 p-4 text-sm font-semibold transition-colors ${data.who === v ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                    data-testid={`who-${v}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-parent">
              <h2 className="font-heading text-xl font-bold text-stone-900">{data.parents.length > 1 ? "About your parents" : `About your ${data.parents[0].label.toLowerCase()}`}</h2>
              {data.parents.map((p, i) => (
                <div key={`${p.label}-${i}`} className="rounded-2xl border border-stone-200 p-4 sm:p-5" data-testid={`parent-form-${i}`}>
                  <p className="mb-4 font-heading text-base font-bold text-emerald-900">
                    {data.parents.length > 1 ? `Parent ${i + 1} — ${p.label}` : p.label}
                  </p>
                  {i === 1 && (
                    <label className="mb-4 flex items-center gap-2.5 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-900" data-testid="same-address-block">
                      <input type="checkbox" className="h-4 w-4 accent-emerald-800" checked={sameAddress} onChange={(e) => toggleSameAddress(e.target.checked)} data-testid="same-address-checkbox" />
                      Same address as Parent 1
                    </label>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2"><label className="p1-label">Full name *</label>
                      <input className="p1-input" value={p.name} onChange={(e) => setParent(i, "name", e.target.value)} data-testid={`parent-${i}-name`} /></div>
                    <div><label className="p1-label">Age</label>
                      <input type="number" className="p1-input" value={p.age} onChange={(e) => setParent(i, "age", e.target.value)} data-testid={`parent-${i}-age`} /></div>
                    <div><label className="p1-label">Gender (optional)</label>
                      <select className="p1-input" value={p.gender} onChange={(e) => setParent(i, "gender", e.target.value)} data-testid={`parent-${i}-gender`}>
                        <option value="">Prefer not to say</option><option>Female</option><option>Male</option><option>Other</option>
                      </select></div>
                    <div className="sm:col-span-2"><label className="p1-label">Home address *</label>
                      <textarea className="p1-input" rows={2} value={p.address} onChange={(e) => setParent(i, "address", e.target.value)} data-testid={`parent-${i}-address`} /></div>
                    <div><label className="p1-label">Area / Zone</label>
                      <select className="p1-input" value={p.zone} onChange={(e) => setParent(i, "zone", e.target.value)} data-testid={`parent-${i}-zone`}>
                        <option value="">Select area</option><option>Vaishali Nagar</option><option>C-Scheme</option><option>Malviya Nagar</option><option>Mansarovar</option><option>Other Jaipur</option>
                      </select></div>
                    <div><label className="p1-label">Preferred language</label>
                      <select className="p1-input" value={p.language} onChange={(e) => setParent(i, "language", e.target.value)} data-testid={`parent-${i}-language`}>
                        <option>Hindi</option><option>Marwari</option><option>English</option>
                      </select></div>
                    <div className="sm:col-span-2"><label className="p1-label">Living arrangement</label>
                      <select className="p1-input" value={p.living_arrangement} onChange={(e) => setParent(i, "living_arrangement", e.target.value)} data-testid={`parent-${i}-living`}>
                        <option>Lives alone</option><option>Lives with spouse</option><option>Lives with extended family</option><option>With part-time help</option>
                      </select></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-family">
              <h2 className="font-heading text-xl font-bold text-stone-900">About you (primary family contact)</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="p1-label">Your name *</label>
                  <input className="p1-input" value={data.family.name} onChange={(e) => set("family", "name", e.target.value)} data-testid="family-name" /></div>
                <div><label className="p1-label">Relationship</label>
                  <select className="p1-input" value={data.family.relation} onChange={(e) => set("family", "relation", e.target.value)} data-testid="family-relation">
                    <option>Son</option><option>Daughter</option><option>Son-in-law</option><option>Daughter-in-law</option><option>Grandchild</option><option>Other</option>
                  </select></div>
                <div><label className="p1-label">Where do you live?</label>
                  <input className="p1-input" placeholder="e.g. Bengaluru, Dubai, London" value={data.family.location} onChange={(e) => set("family", "location", e.target.value)} data-testid="family-location" /></div>
                <div><label className="p1-label">Mobile *</label>
                  <input className="p1-input" value={data.family.phone} onChange={(e) => set("family", "phone", e.target.value)} data-testid="family-phone" /></div>
                <div><label className="p1-label">WhatsApp (if different)</label>
                  <input className="p1-input" value={data.family.whatsapp} onChange={(e) => set("family", "whatsapp", e.target.value)} data-testid="family-whatsapp" /></div>
                <div><label className="p1-label">Email</label>
                  <input type="email" className="p1-input" value={data.family.email} onChange={(e) => set("family", "email", e.target.value)} data-testid="family-email" /></div>
                <div className="sm:col-span-2"><label className="p1-label">Other family members to involve (names & cities)</label>
                  <textarea className="p1-input" rows={2} value={data.family.secondary_members} onChange={(e) => set("family", "secondary_members", e.target.value)} data-testid="family-secondary" /></div>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="wa-optin-block">
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-800" checked={!!data.whatsapp_opt_in} onChange={(e) => setData({ ...data, whatsapp_opt_in: e.target.checked })} data-testid="wa-optin-checkbox" />
                    <span className="text-sm text-emerald-950">Send me important service updates on WhatsApp (request confirmations, status updates) at the number above. You can opt out anytime.</span>
                  </label>
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div data-testid="step-needs">
              <h2 className="font-heading text-xl font-bold text-stone-900">What kind of support would help? <span className="text-sm font-normal text-stone-500">(choose any)</span></h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {NEEDS.map(([k, l]) => (
                  <button key={k} onClick={() => toggleNeed(k)}
                    className={`rounded-2xl border-2 p-3.5 text-left text-sm font-semibold transition-colors ${data.care_needs.includes(k) ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                    data-testid={`need-${k}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-info">
              <h2 className="font-heading text-xl font-bold text-stone-900">Important information <span className="text-sm font-normal text-stone-500">(all optional)</span></h2>
              <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">Medical details are sensitive and entirely optional. They're stored only with your explicit consent, and access is permission-controlled and audited.</p>
              {data.parents.length > 1 && (
                <div><label className="p1-label">This information relates to</label>
                  <select className="p1-input" value={data.medical_for} onChange={(e) => setData({ ...data, medical_for: e.target.value })} data-testid="medical-for-select">
                    <option value="0">{data.parents[0].label}{data.parents[0].name ? ` — ${data.parents[0].name}` : ""}</option>
                    <option value="1">{data.parents[1].label}{data.parents[1].name ? ` — ${data.parents[1].name}` : ""}</option>
                    <option value="both">Both parents</option>
                  </select></div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="p1-label">Preferred hospital</label>
                  <input className="p1-input" placeholder="e.g. SMS Hospital" value={data.info.preferred_hospital} onChange={(e) => set("info", "preferred_hospital", e.target.value)} data-testid="info-hospital" /></div>
                <div><label className="p1-label">Regular doctors</label>
                  <input className="p1-input" placeholder="e.g. Dr. Joshi (Cardiology)" value={data.info.doctors} onChange={(e) => set("info", "doctors", e.target.value)} data-testid="info-doctors" /></div>
                <div><label className="p1-label">Medical conditions</label>
                  <input className="p1-input" placeholder="comma separated" value={data.info.conditions} onChange={(e) => set("info", "conditions", e.target.value)} data-testid="info-conditions" /></div>
                <div><label className="p1-label">Allergies</label>
                  <input className="p1-input" value={data.info.allergies} onChange={(e) => set("info", "allergies", e.target.value)} data-testid="info-allergies" /></div>
                <div><label className="p1-label">Current medications</label>
                  <input className="p1-input" value={data.info.medications} onChange={(e) => set("info", "medications", e.target.value)} data-testid="info-medications" /></div>
                <div><label className="p1-label">Health insurance</label>
                  <input className="p1-input" placeholder="Provider" value={data.info.insurance_provider} onChange={(e) => set("info", "insurance_provider", e.target.value)} data-testid="info-insurance" /></div>
                <div className="sm:col-span-2 grid gap-4 rounded-2xl border border-stone-200 p-4 sm:grid-cols-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 sm:col-span-3">Local emergency contact (near your parents)</p>
                  <input className="p1-input" placeholder="Name" value={data.info.ec1_name} onChange={(e) => set("info", "ec1_name", e.target.value)} data-testid="info-ec-name" />
                  <input className="p1-input" placeholder="Phone" value={data.info.ec1_phone} onChange={(e) => set("info", "ec1_phone", e.target.value)} data-testid="info-ec-phone" />
                  <input className="p1-input" placeholder="Relation" value={data.info.ec1_relation} onChange={(e) => set("info", "ec1_relation", e.target.value)} data-testid="info-ec-relation" />
                </div>
              </div>
              {hasMedical() && (
                <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="consent-block">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-800" checked={data.consent_medical} onChange={(e) => setData({ ...data, consent_medical: e.target.checked })} data-testid="consent-checkbox" />
                  <span className="text-sm text-emerald-950">I consent to ParentOne storing this medical information to coordinate care, with permission-controlled, audited access.</span>
                </label>
              )}
            </div>
          )}
          {step === 5 && (
            <div data-testid="step-assessment">
              <h2 className="font-heading text-xl font-bold text-stone-900">Book your free Parent Care Assessment</h2>
              <p className="mt-2 text-sm text-stone-600">A ParentOne care advisor will call you to understand your parents' situation and recommend the right plan. Free, ~20 minutes, no obligation.</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {["Weekday morning", "Weekday evening", "Weekend"].map((s) => (
                  <button key={s} onClick={() => setData({ ...data, assessment_slot: s })}
                    className={`rounded-2xl border-2 p-4 text-sm font-semibold transition-colors ${data.assessment_slot === s ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                    data-testid={`slot-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</button>
                ))}
              </div>
              <div className="mt-5"><TurnstileField onToken={setTsToken} /></div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
          className="p1-btn-outline disabled:opacity-40" data-testid="onboarding-back"><ChevronLeft className="h-4 w-4" /> Back</button>
        {step < 5 ? (
          <button onClick={() => valid() ? setStep(step + 1) : toast.error("Please fill the required fields (and consent, if medical info is added).")}
            className="p1-btn-primary" data-testid="onboarding-next">Continue <ChevronRight className="h-4 w-4" /></button>
        ) : (
          <button onClick={submit} disabled={busy} className="p1-btn-primary" data-testid="onboarding-submit">
            {busy ? "Submitting…" : "Book Free Assessment"} <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
